import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Ticket, Gift, Wallet, ArrowRight, ExternalLink, Trophy, RefreshCw, Users, Clock } from "lucide-react";
import { DashboardLayout } from "../components/DashboardLayout";
import { BasarNav } from "../components/basar/BasarNav";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Skeleton } from "../components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { api, imageSrc } from "../lib/api";
import { basarStatusMeta, basarUrl, dashboardBasarUrl, nok, soldPercent } from "../lib/basar";
import { useLang } from "../context/LanguageContext";

const ORDER = { open: 0, closed: 1, draft: 2, drawn: 3 };

// /dashboard/basar – arrangøren sine basarar (aktive + trekte) med nøkkeltal og inngang til kvar basar
export default function BasarDashboardPage() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = () => {
    setLoading(true);
    setError(false);
    api.get("/my/basar").then((res) => setData(res.data)).catch(() => setError(true)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const basars = [...(data?.basars || [])].sort((a, b) => (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9) || (b.created_at || "").localeCompare(a.created_at || ""));
  const active = basars.filter((b) => b.status !== "drawn");
  const drawn = basars.filter((b) => b.status === "drawn");

  const card = (b) => {
    const st = basarStatusMeta(b);
    const m = b.metrics || {};
    return (
      <Card key={b.id} className="border-border overflow-hidden flex flex-col" data-testid="basar-admin-card" data-basar-id={b.id} data-status={b.status}>
        {b.image_url && <div className="aspect-[16/6] bg-muted overflow-hidden"><img src={imageSrc(b.image_url)} alt="" className="h-full w-full object-cover" /></div>}
        <CardContent className="p-5 space-y-4 flex-1 flex flex-col">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Link to={dashboardBasarUrl(b.id)} className="font-heading font-semibold leading-snug hover:underline underline-offset-4 line-clamp-2" data-testid="basar-admin-card-name">{b.name}</Link>
              <p className="text-xs text-muted-foreground mt-1 tabular-nums">{t("Nummer {from}–{to}", { from: b.series_from, to: b.series_to })} · {t("{amount} per lodd", { amount: nok(b.price) })}</p>
            </div>
            <Badge className={`${st.className} shrink-0`} data-testid="basar-admin-card-status">{st.label}</Badge>
          </div>
          <div className="space-y-1.5">
            <Progress value={soldPercent(m)} className="h-2 bg-[hsl(var(--warning))]/15 [&>div]:bg-[hsl(var(--warning))]" />
            <div className="flex justify-between text-xs text-muted-foreground tabular-nums">
              <span data-testid="basar-admin-card-sold">{t("Solgt {sold} av {total}", { sold: m.sold ?? 0, total: m.total ?? b.total ?? 0 })}</span>
              <span data-testid="basar-admin-card-revenue">{nok(m.revenue || 0)}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><Gift className="w-3.5 h-3.5" /> {b.prizes?.length === 1 ? t("{n} premie", { n: 1 }) : t("{n} premier", { n: b.prizes?.length || 0 })}</span>
            <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {m.buyers === 1 ? t("{n} kjøper", { n: 1 }) : t("{n} kjøpere", { n: m.buyers || 0 })}</span>
            {m.pending > 0 && <span className="inline-flex items-center gap-1 text-[hsl(var(--info))]" data-testid="basar-admin-card-pending"><Clock className="w-3.5 h-3.5" /> {t("{n} venter på Vipps", { n: m.pending })}</span>}
            {b.status === "drawn" && <span className="inline-flex items-center gap-1"><Trophy className="w-3.5 h-3.5 text-[hsl(var(--warning))]" /> {t("Vinnerne er publisert")}</span>}
          </div>
          <div className="mt-auto flex items-center justify-between gap-2 pt-1">
            <Button asChild size="sm" data-testid="basar-admin-card-open"><Link to={dashboardBasarUrl(b.id)}>{t("Åpne")} <ArrowRight className="w-4 h-4 ml-1.5" /></Link></Button>
            {b.status !== "draft" && <Button asChild variant="ghost" size="sm" data-testid="basar-admin-card-public"><a href={basarUrl(b.id)} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-1.5" /> {t("Offentlig side")}</a></Button>}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="basar-dashboard-page">
        <BasarNav actions={<Button asChild data-testid="basar-new-button"><Link to="/dashboard/basar/ny"><Plus className="w-4 h-4 mr-1.5" /> {t("Ny basar")}</Link></Button>} />

        {data && !data.vipps_available && (
          <Alert className="bg-[hsl(var(--warning))]/10 border-[hsl(var(--warning))]/30" data-testid="basar-vipps-warning">
            <Wallet className="h-4 w-4" />
            <AlertTitle>{t("Vipps er ikke satt opp")}</AlertTitle>
            <AlertDescription>
              {t("Publikum kan ikke kjøpe lodd på nett før Vipps er klart – pengene går rett inn på deres egen Vipps-salgsenhet. Manuelle salg (kontant / Vipps ved døren) kan registreres uansett.")}{" "}
              <Link to="/dashboard/betaling" className="underline underline-offset-4 font-medium" data-testid="basar-vipps-setup-link">{t("Gå til Betaling")}</Link>
            </AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="basar-loading-state">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-64 rounded-xl" />)}</div>
        ) : error ? (
          <Alert variant="destructive" data-testid="basar-error-state">
            <AlertTitle>{t("Kunne ikke laste basarene")}</AlertTitle>
            <AlertDescription className="flex items-center justify-between gap-3">{t("Prøv igjen om litt.")} <Button size="sm" variant="outline" onClick={load} data-testid="basar-retry-button"><RefreshCw className="w-4 h-4 mr-1.5" /> {t("Prøv igjen")}</Button></AlertDescription>
          </Alert>
        ) : basars.length === 0 ? (
          <Card className="border-dashed border-border" data-testid="basar-empty-state">
            <CardContent className="p-10 text-center space-y-4">
              <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]"><Ticket className="w-7 h-7" /></span>
              <div className="space-y-2 max-w-xl mx-auto">
                <h2 className="text-lg font-heading font-semibold">{t("Loddsalg med egne nummer, Vipps og digital trekning")}</h2>
                <p className="text-sm text-muted-foreground">{t("Sett opp en nummerserie (f.eks. 1–1000), pris per lodd og premier. Kjøperne velger numrene sine selv og betaler med Vipps – eller dere registrerer salg ved døren. Trekningen gjøres her i Right in, og vinnerlisten publiseres på basarsiden.")}</p>
              </div>
              <Button asChild data-testid="basar-empty-create-button"><Link to="/dashboard/basar/ny"><Plus className="w-4 h-4 mr-1.5" /> {t("Opprett din første basar")}</Link></Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {active.length > 0 && (
              <section className="space-y-4" data-testid="basar-admin-active-section">
                <h2 className="text-lg font-heading font-semibold tracking-tight">{t("Aktive basarer")}</h2>
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{active.map(card)}</div>
              </section>
            )}
            {drawn.length > 0 && (
              <section className="space-y-4" data-testid="basar-admin-drawn-section">
                <h2 className="text-lg font-heading font-semibold tracking-tight">{t("Trukne basarer")}</h2>
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{drawn.map(card)}</div>
              </section>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
